import React, { useState } from 'react'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import { useTranslation } from 'react-i18next'

import { Dialog, snackbar, ToggleSwitch } from 'components'
import { useApi, useApiCall } from 'hooks'
import { ERRORS } from 'consts'

import { getMemberships } from '../../Memberships/api'
import { Membership, GetMembershipsResponse } from '../../Memberships/types'
import { getActiveTags } from '../../Tags/api'
import { Tag, GetTagsResponse } from '../../Tags/types'
import { getActiveStores } from '../../Stores/api'
import { Store, GetStoresResponse } from '../../Stores/types'
import { createParkingConfig } from '../api'
import {
  ParkingConfig,
  ParkingConfigFormPayload,
  ParkingConfigResponse,
} from '../types'
import { getParkingConfigErrorToast } from '../errorMessage'
import { ParkingConfigForm } from './ParkingConfigForm'
import { useStyles } from './styles'

interface CreateParkingConfigDialogProps {
  handleClose: () => void
  onDone: (parkingConfig: ParkingConfig) => void
}

const getMembershipsResponseGetter = (responseData: GetMembershipsResponse) =>
  responseData?.memberships ?? []
const getTagsResponseGetter = (responseData: GetTagsResponse) => responseData?.tags ?? []
const getStoresResponseGetter = (responseData: GetStoresResponse) => responseData?.stores ?? []

const CreateParkingConfigDialog: React.FC<CreateParkingConfigDialogProps> = ({
  handleClose,
  onDone,
}) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const [active, setActive] = useState(false)
  const [{ data: memberships }] = useApi<GetMembershipsResponse, Membership[]>(
    getMemberships,
    getMembershipsResponseGetter,
    { baseData: [], onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE) },
  )
  const [{ data: tags }] = useApi<GetTagsResponse, Tag[]>(
    () => getActiveTags({ group: 'BRANDS' }),
    getTagsResponseGetter,
    {
      baseData: [],
      onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
    },
  )
  const [{ data: stores }] = useApi<GetStoresResponse, Store[]>(
    () => getActiveStores(),
    getStoresResponseGetter,
    {
      baseData: [],
      onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
    },
  )

  const [createParkingConfigApi, isLoading] = useApiCall<
    ParkingConfigFormPayload,
    ParkingConfigResponse
  >(createParkingConfig)

  const handleSubmit = async (payload: ParkingConfigFormPayload) => {
    try {
      const { parkingConfig } = await createParkingConfigApi(payload)
      onDone(parkingConfig)
    } catch (err) {
      console.error(err)
      const { message, variant } = getParkingConfigErrorToast(err, 'parking:errors.createFailed')
      snackbar.show(message, variant)
    }
  }

  return (
    <Dialog
      title={t('parking:createTitle') as string}
      isOpen
      showActions
      onCancel={handleClose}
      isLoading={isLoading}
      okButtonText={t('parking:create') as string}
      okButtonProps={{ disabled: isLoading }}
      style={{ width: 720, maxWidth: 'calc(100vw - 32px)' }}
      contentStyle={{ width: '100%', boxSizing: 'border-box', paddingTop: 8 }}
      overflow
      actionsAccessory={
        <FormControlLabel
          className={classes.actionsActiveToggle}
          labelPlacement="start"
          label={t('parking:fields.active') as string}
          control={
            <ToggleSwitch
              checked={active}
              onChange={event => setActive(event.target.checked)}
              disabled={isLoading}
            />
          }
        />
      }
      onAccept={() => {
        const form = document.getElementById('create-parking-config-form') as HTMLFormElement | null
        form?.requestSubmit()
      }}
    >
      <ParkingConfigForm
        formId="create-parking-config-form"
        isCreate
        isLoading={isLoading}
        memberships={memberships}
        tags={tags}
        stores={stores}
        active={active}
        onSubmit={handleSubmit}
      />
    </Dialog>
  )
}

export { CreateParkingConfigDialog }
